/**
 * Full-viewport attribute table workspace.
 *
 * Session-only header actions (sort, hide, freeze, calculate, statistics)
 * stay in this dialog and do not write back to the layer source.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Icon, IconButton, Stack, Tabs, Text, tokens } from '@/design';
import {
  VectorAttributeTable,
  type VectorAttributeTableProps,
} from './VectorAttributeTable';
import {
  AttributeHeaderMenu,
  type AttributeHeaderAction,
} from './AttributeHeaderMenu';
import { AttributeChartPanel } from './AttributeChartPanel';
import {
  AttributeCalculateFieldDialog,
  AttributeCalculateGeometryDialog,
  AttributeCalculateToolbar,
  AttributeCustomSortDialog,
  AttributeExploreStatsDialog,
  AttributeFieldsDialog,
  AttributeSummarizeDialog,
} from './AttributeTableDialogs';
import {
  TABLE_TAB_ID,
  TABLE_WORKSPACE_TAB,
  SELECTED_RECORDS_TAB_ID,
  attributeRowKey,
  canHideColumn,
  cloneAttributeRows,
  closeWorkspaceTab,
  createChartTab,
  createEmptyAttributeRow,
  createSelectedRecordsTab,
  createSummaryTab,
  filterRowsBySelectedKeys,
  freezeThrough,
  hideColumn,
  invertSelectedRowKeys,
  mergeColumnOrder,
  nextAddedRowId,
  sameStringList,
  singleColumnSort,
  sortAttributeRows,
  sourceFingerprint,
  toggleSelectedRowKey,
  updateAttributeCell,
  upsertWorkspaceTab,
  visibleColumns,
  type SortLevel,
  type TableWorkspaceTab,
} from './attributeTableModel';
import { exploreColumnStats, visualizeColumnCounts } from './attributeTableStats';
import { summarizeAttributeRows } from './attributeTableSummarize';
import { applyFieldExpression } from './attributeFieldExpression';
import {
  applyGeometryColumn,
  featuresHaveGeometry,
  type GeometryProperty,
  type GeometryUnit,
} from './attributeGeometryCalc';

export interface VectorAttributeTableDialogProps
  extends Omit<VectorAttributeTableProps, 'variant' | 'hideHeading' | 'columns' | 'features' | 'toolbar' | 'onHeaderMenu' | 'sortSpec' | 'frozenColumns' | 'onUpdateCell' | 'onAddRow'> {
  readonly title: string;
  readonly onClose: () => void;
  readonly features: VectorAttributeTableProps['features'];
  readonly columns: ReadonlyArray<string>;
  readonly onSelectionChange?: (
    rows: ReadonlyArray<VectorAttributeTableProps['features'][number]>,
  ) => void;
  readonly onZoomToRows?: (
    rows: ReadonlyArray<VectorAttributeTableProps['features'][number]>,
  ) => void;
}

type SecondaryDialog =
  | 'custom-sort'
  | 'fields'
  | 'explore'
  | 'summarize'
  | 'calc-field'
  | 'calc-geom'
  | null;

export function VectorAttributeTableDialog({
  title,
  onClose,
  pageSize = 25,
  features,
  columns,
  onSelectionChange,
  onZoomToRows,
  ...tableProps
}: VectorAttributeTableDialogProps) {
  const testId = tableProps['data-testid'] ?? 'vector-attribute-table-dialog';
  const [workingRows, setWorkingRows] = useState(() => cloneAttributeRows(features));
  const [columnOrder, setColumnOrder] = useState(() => [...columns]);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [frozen, setFrozen] = useState<string[]>([]);
  const [sortSpec, setSortSpec] = useState<SortLevel[]>([]);
  const [tabs, setTabs] = useState<TableWorkspaceTab[]>([TABLE_WORKSPACE_TAB]);
  const [activeTab, setActiveTab] = useState(TABLE_TAB_ID);
  const [menu, setMenu] = useState<{ column: string; x: number; y: number } | null>(null);
  const [activeColumn, setActiveColumn] = useState(columns[0] ?? '');
  const [secondary, setSecondary] = useState<SecondaryDialog>(null);
  const [toolbarOpen, setToolbarOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const [minimized, setMinimized] = useState(false);
  const sourceKey = sourceFingerprint(features);

  useEffect(() => {
    setWorkingRows(cloneAttributeRows(features));
    setSelectedRowKeys(new Set());
    setTabs([TABLE_WORKSPACE_TAB]);
    setActiveTab(TABLE_TAB_ID);
    // Resync only when the loaded layer identity changes, not when the
    // parent passes a new array instance after a row click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey]);

  useEffect(() => {
    setColumnOrder((prev) => {
      const next = mergeColumnOrder(prev, columns);
      return sameStringList(prev, next) ? prev : next;
    });
  }, [columns]);

  const visible = useMemo(() => visibleColumns(columnOrder, hidden), [columnOrder, hidden]);
  const displayedRows = useMemo(
    () => sortAttributeRows(workingRows, sortSpec),
    [workingRows, sortSpec],
  );
  const selectedRows = useMemo(
    () => filterRowsBySelectedKeys(displayedRows, selectedRowKeys),
    [displayedRows, selectedRowKeys],
  );
  const hasGeometry = useMemo(() => featuresHaveGeometry(workingRows), [workingRows]);
  const exploreStats = useMemo(
    () => (activeColumn ? exploreColumnStats(workingRows, activeColumn) : null),
    [workingRows, activeColumn],
  );
  const currentTab = tabs.find((tab) => tab.id === activeTab) ?? TABLE_WORKSPACE_TAB;
  const activeTableRows =
    currentTab.kind === 'selected' ? selectedRows : displayedRows;

  useEffect(() => {
    onSelectionChange?.(selectedRows);
  }, [onSelectionChange, selectedRows]);

  const addCalculatedColumn = useCallback((name: string) => {
    setColumnOrder((prev) => mergeColumnOrder(prev, [name]));
    setHidden((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  }, []);

  const handleUpdateCell = useCallback(
    (row: VectorAttributeTableProps['features'][number], column: string, value: unknown) => {
      setWorkingRows((prev) => updateAttributeCell(prev, row, column, value));
    },
    [],
  );

  const handleAddRow = useCallback(() => {
    setWorkingRows((prev) => [
      ...prev,
      createEmptyAttributeRow(columnOrder, nextAddedRowId(prev)),
    ]);
  }, [columnOrder]);

  const handleToggleRowSelection = useCallback(
    (rowKey: string, _row: VectorAttributeTableProps['features'][number], selected: boolean) => {
      setSelectedRowKeys((prev) => toggleSelectedRowKey(prev, rowKey, selected));
    },
    [],
  );

  const handleToggleAllRows = useCallback(
    (
      rows: ReadonlyArray<VectorAttributeTableProps['features'][number]>,
      selected: boolean,
    ) => {
      setSelectedRowKeys((prev) => {
        const next = new Set(prev);
        rows.forEach((row, index) => {
          const key = attributeRowKey(row, index);
          if (selected) next.add(key);
          else next.delete(key);
        });
        return next;
      });
    },
    [],
  );

  const showSelectedRecords = useCallback(() => {
    if (selectedRows.length === 0) return;
    const tab = createSelectedRecordsTab(selectedRows.length);
    setTabs((prev) => upsertWorkspaceTab(prev, tab));
    setActiveTab(SELECTED_RECORDS_TAB_ID);
  }, [selectedRows.length]);

  const clearSelection = useCallback(() => {
    setSelectedRowKeys(new Set());
    setTabs((prev) => prev.filter((tab) => tab.id !== SELECTED_RECORDS_TAB_ID));
    setActiveTab((current) =>
      current === SELECTED_RECORDS_TAB_ID ? TABLE_TAB_ID : current,
    );
  }, []);

  const closeDialog = useCallback(() => {
    onSelectionChange?.([]);
    onClose();
  }, [onClose, onSelectionChange]);

  useEffect(() => {
    setTabs((prev) =>
      prev.some((tab) => tab.id === SELECTED_RECORDS_TAB_ID)
        ? upsertWorkspaceTab(prev, createSelectedRecordsTab(selectedRows.length))
        : prev,
    );
  }, [selectedRows.length]);

  const applyExpression = useCallback(
    (targetField: string, expression: string) => {
      const result = applyFieldExpression(workingRows, targetField, expression);
      if (!result.ok) return result.error;
      setWorkingRows(result.rows);
      addCalculatedColumn(targetField.trim());
      setSecondary(null);
      return null;
    },
    [addCalculatedColumn, workingRows],
  );

  const handleHeaderAction = useCallback(
    (action: AttributeHeaderAction, column: string) => {
      setActiveColumn(column);
      switch (action) {
        case 'sort-asc':
          setSortSpec(singleColumnSort(column, 'asc'));
          break;
        case 'sort-desc':
          setSortSpec(singleColumnSort(column, 'desc'));
          break;
        case 'custom-sort':
          setSecondary('custom-sort');
          break;
        case 'fields':
          setSecondary('fields');
          break;
        case 'hide':
          setHidden((prev) => {
            const nextVisible = hideColumn(visibleColumns(columnOrder, prev), column);
            return new Set(columnOrder.filter((name) => !nextVisible.includes(name)));
          });
          setFrozen((prev) => prev.filter((name) => name !== column));
          break;
        case 'freeze':
          setFrozen((prev) => (prev.includes(column) ? [] : freezeThrough(visible, column)));
          break;
        case 'calculate-field':
          setSecondary('calc-field');
          break;
        case 'calculate-toolbar':
          setToolbarOpen(true);
          break;
        case 'calculate-geometry':
          setSecondary('calc-geom');
          break;
        case 'explore-stats':
          setSecondary('explore');
          break;
        case 'visualize-stats': {
          const tab = createChartTab(column);
          setTabs((prev) => upsertWorkspaceTab(prev, tab));
          setActiveTab(tab.id);
          break;
        }
        case 'summarize':
          setSecondary('summarize');
          break;
        default:
          break;
      }
    },
    [columnOrder, visible],
  );

  const closeExtraTab = useCallback((tabId: string) => {
    setTabs((prev) => {
      const next = closeWorkspaceTab(prev, tabId, activeTab);
      setActiveTab(next.activeId);
      return next.tabs;
    });
  }, [activeTab]);

  const selectionToolbar = (
    <Stack
      direction="row"
      align="center"
      gap={2}
      style={{ flexWrap: 'wrap', flexShrink: 0 }}
      data-testid={`${testId}-selection-toolbar`}
    >
      <Text variant="caption" as="span" aria-live="polite">
        <strong>{selectedRows.length}</strong> selected
      </Text>
      <Button
        variant="secondary"
        disabled={selectedRows.length === 0}
        onClick={showSelectedRecords}
        data-testid={`${testId}-show-selected`}
      >
        Show selected records
      </Button>
      <Button
        variant="secondary"
        disabled={selectedRows.length === 0 || !onZoomToRows}
        onClick={() => onZoomToRows?.(selectedRows)}
        data-testid={`${testId}-zoom-selected`}
      >
        Zoom to selected
      </Button>
      <Button
        variant="secondary"
        disabled={displayedRows.length === 0 || !onZoomToRows}
        onClick={() => onZoomToRows?.(displayedRows)}
        data-testid={`${testId}-zoom-layer`}
      >
        Zoom to layer
      </Button>
      <Button
        variant="secondary"
        disabled={displayedRows.length === 0}
        onClick={() =>
          setSelectedRowKeys((prev) => invertSelectedRowKeys(displayedRows, prev))
        }
        data-testid={`${testId}-switch-selection`}
      >
        Switch selection
      </Button>
      <Button
        variant="secondary"
        disabled={selectedRows.length === 0}
        onClick={clearSelection}
        data-testid={`${testId}-clear-selection`}
      >
        Clear
      </Button>
    </Stack>
  );

  const body = minimized ? (
    <div
      role="dialog"
      aria-label={`${title} attribute table minimized`}
      data-testid={`${testId}-minimized`}
      style={{
        position: 'fixed',
        left: tokens.space[4],
        right: tokens.space[4],
        bottom: tokens.space[3],
        zIndex: tokens.zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        minWidth: 0,
        minHeight: tokens.density.touchMin,
        padding: tokens.space[2],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        background: tokens.color.surface[1],
        color: tokens.color.ink.primary,
        boxShadow: tokens.elevation[3],
      }}
    >
      <Icon name="table" size={20} decorative />
      <Text
        variant="body"
        as="strong"
        title={title}
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title} · {selectedRows.length} selected
      </Text>
      <IconButton
        aria-label="Restore attribute table"
        onClick={() => setMinimized(false)}
        data-testid={`${testId}-restore`}
      >
        <Icon name="expand_less" size={20} decorative />
      </IconButton>
      <IconButton
        aria-label="Close attribute table"
        onClick={closeDialog}
        data-testid={`${testId}-minimized-close`}
      >
        <Icon name="close" size={20} decorative />
      </IconButton>
    </div>
  ) : (
    <div
      role="presentation"
      data-testid={`${testId}-backdrop`}
      onClick={(event) => {
        if (event.target === event.currentTarget) closeDialog();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: tokens.zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: tokens.space[4],
        background: tokens.color.overlay.scrim,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        onKeyDown={(event) => {
          if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 's') {
            event.preventDefault();
            setSecondary('custom-sort');
          }
          if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === 'f') {
            event.preventDefault();
            setToolbarOpen(true);
          }
        }}
        style={{
          width: '96vw',
          height: '92vh',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          gap: tokens.space[3],
          padding: tokens.space[4],
          background: tokens.color.surface[1],
          color: tokens.color.ink.primary,
          borderRadius: tokens.radius.lg,
          boxShadow: tokens.elevation[3],
          border: tokens.border.thin,
          minHeight: 0,
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            minHeight: tokens.density.touchMin,
            flexShrink: 0,
          }}
        >
          <Icon name="table" size={24} decorative />
          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
            <Text variant="subtitle" as="h2" style={{ margin: 0 }}>
              {title}
            </Text>
            <Text variant="caption" as="p" style={{ margin: 0, color: tokens.color.ink.secondary }}>
              Attribute table
            </Text>
          </div>
          <IconButton
            aria-label="Minimize attribute table"
            onClick={() => setMinimized(true)}
            data-testid={`${testId}-minimize`}
          >
            <Icon name="expand_more" size={20} decorative />
          </IconButton>
          <IconButton
            aria-label="Close attribute table"
            onClick={closeDialog}
            data-testid={`${testId}-close`}
          >
            <Icon name="close" size={20} decorative />
          </IconButton>
        </header>

        <div style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {selectionToolbar}
          <Tabs
            tabs={tabs.map((tab) => ({ id: tab.id, label: tab.label }))}
            value={activeTab}
            onChange={setActiveTab}
            aria-label="Attribute table workspace"
            data-testid={`${testId}-tabs`}
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
            panelStyle={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
              paddingTop: tokens.space[3],
            }}
          >
            {currentTab.kind === 'chart' && currentTab.column ? (
              <AttributeChartPanel
                series={visualizeColumnCounts(workingRows, currentTab.column)}
                onClose={() => closeExtraTab(currentTab.id)}
                data-testid={`${testId}-chart`}
              />
            ) : currentTab.kind === 'summary' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.space[2], minHeight: 0, flex: '1 1 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    aria-label="Close summary"
                    onClick={() => closeExtraTab(currentTab.id)}
                    data-testid={`${testId}-summary-close`}
                  >
                    <Icon name="close" size={20} decorative />
                  </IconButton>
                </div>
                <VectorAttributeTable
                  features={currentTab.summaryRows ?? []}
                  columns={currentTab.summaryColumns ?? []}
                  pageSize={pageSize}
                  variant="dialog"
                  hideHeading
                  data-testid={`${testId}-summary`}
                />
              </div>
            ) : (
              <VectorAttributeTable
                {...tableProps}
                features={activeTableRows}
                columns={visible}
                pageSize={pageSize}
                variant="dialog"
                hideHeading
                sortSpec={sortSpec}
                frozenColumns={frozen}
                state={
                  tableProps.state === 'loading' && activeTableRows.length === 0
                    ? 'loading'
                    : tableProps.state === 'error' && activeTableRows.length === 0
                      ? 'error'
                      : activeTableRows.length === 0
                        ? 'empty'
                        : 'idle'
                }
                selectedRowKeys={selectedRowKeys}
                onToggleRowSelection={handleToggleRowSelection}
                onToggleAllRows={handleToggleAllRows}
                onHeaderMenu={(column, position) => {
                  setActiveColumn(column);
                  setMenu({ column, ...position });
                }}
                onUpdateCell={handleUpdateCell}
                onAddRow={handleAddRow}
                data-testid={
                  currentTab.kind === 'selected'
                    ? `${testId}-selected-records`
                    : tableProps['data-testid']
                }
                toolbar={
                  toolbarOpen ? (
                    <AttributeCalculateToolbar
                      columns={columnOrder}
                      initialField={activeColumn}
                      onApply={applyExpression}
                      onClose={() => setToolbarOpen(false)}
                    />
                  ) : null
                }
              />
            )}
          </Tabs>
        </div>
      </div>

      {menu ? (
        <AttributeHeaderMenu
          column={menu.column}
          x={menu.x}
          y={menu.y}
          frozen={frozen.includes(menu.column)}
          canHide={canHideColumn(visible.length)}
          hasGeometry={hasGeometry}
          onAction={handleHeaderAction}
          onClose={() => setMenu(null)}
          data-testid={`${testId}-header-menu`}
        />
      ) : null}

      {secondary === 'custom-sort' ? (
        <AttributeCustomSortDialog
          columns={visible}
          initial={sortSpec.length > 0 ? sortSpec : singleColumnSort(activeColumn, 'asc')}
          onApply={(spec) => {
            setSortSpec(spec);
            setSecondary(null);
          }}
          onClose={() => setSecondary(null)}
        />
      ) : null}
      {secondary === 'fields' ? (
        <AttributeFieldsDialog
          order={columnOrder}
          hidden={hidden}
          onChange={(next) => {
            setColumnOrder(next.order);
            setHidden(next.hidden);
            setFrozen((prev) => prev.filter((name) => next.order.includes(name) && !next.hidden.has(name)));
            setSecondary(null);
          }}
          onClose={() => setSecondary(null)}
        />
      ) : null}
      {secondary === 'explore' && exploreStats ? (
        <AttributeExploreStatsDialog
          stats={exploreStats}
          onClose={() => setSecondary(null)}
        />
      ) : null}
      {secondary === 'summarize' ? (
        <AttributeSummarizeDialog
          columns={visible}
          rows={workingRows}
          initialField={activeColumn}
          onApply={(groupBy, aggregates) => {
            const result = summarizeAttributeRows(workingRows, { groupBy, aggregates });
            const tab = createSummaryTab(groupBy, result.columns, result.rows);
            setTabs((prev) => upsertWorkspaceTab(prev, tab));
            setActiveTab(tab.id);
            setSecondary(null);
          }}
          onClose={() => setSecondary(null)}
        />
      ) : null}
      {secondary === 'calc-field' ? (
        <AttributeCalculateFieldDialog
          columns={columnOrder}
          initialField={activeColumn}
          onApply={applyExpression}
          onClose={() => setSecondary(null)}
        />
      ) : null}
      {secondary === 'calc-geom' ? (
        <AttributeCalculateGeometryDialog
          initialField={activeColumn}
          onApply={(property: GeometryProperty, unit: GeometryUnit, outputField: string) => {
            const result = applyGeometryColumn(workingRows, property, unit, outputField);
            if (!result.ok) return result.error;
            setWorkingRows(result.rows);
            addCalculatedColumn(outputField.trim());
            setSecondary(null);
            return null;
          }}
          onClose={() => setSecondary(null)}
        />
      ) : null}
    </div>
  );

  if (typeof document === 'undefined') return body;
  return createPortal(body, document.body);
}
